import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../state/auth/useAuth';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  
  // Safely get auth data with fallbacks
  const authData = useAuth() || {};
  const { user = null, role = null } = authData;
  
  // Check if user is admin (flexible check)
  const isAdmin = role === 'Admin' || 
                  role === 'admin' || 
                  (user && user.email && user.email.includes('admin'));

  const allNavigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      description: 'Overview and quick access'
    },
    {
      label: 'Access Management',
      path: '/access-management',
      icon: 'Shield',
      description: 'Manage users, roles, and permissions'
    },
    {
      label: 'Inventory',
      path: '/inventory',
      icon: 'Package',
      description: 'Manage hospital inventory and stock'
    },
    {
      label: 'HR Management',
      path: '/hr',
      icon: 'Briefcase',
      description: 'Attendance, salary, and payroll controls'
    },
    {
      label: 'Master Data',
      path: '/admin/dashboard',
      icon: 'Database',
      description: 'Manage medicines, diseases, and other master data'
    },
    {
      label: 'Accounting',
      path: '/accounting',
      icon: 'Calculator',
      description: 'Chart of accounts, banks, vendors, expenses'
    },
    {
      label: 'Settings',
      path: '/hospital-settings',
      icon: 'Settings',
      description: 'Hospital information and invoice configuration'
    },
    // Master Data Section
   
  ];

  // Filter navigation items based on role
  const navigationItems = allNavigationItems.filter(item => {
    // For now, show all items - we can add role filtering later if needed
    return true;
  });



  const closeMobileSidebar = () => setIsMobileOpen(false);

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`fixed left-0 top-16 bottom-0 z-40 bg-card border-r border-border transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-60'
      } hidden lg:block`}>
        <div className="flex flex-col h-full">
          {/* Collapse Toggle */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            {!isCollapsed && (
              <span className="text-sm font-medium text-muted-foreground">Navigation</span>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="h-8 w-8"
            >
              <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} />
            </Button>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 p-3 space-y-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`group flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-clinical relative ${
                  (location?.pathname + location?.search) === item?.path
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon name={item?.icon} size={18} className="flex-shrink-0" />
                {!isCollapsed && (
                  <>
                    <span className="ml-3 flex-1">{item?.label}</span>
                    {item?.badge && (
                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                        location?.pathname === item?.path
                          ? 'bg-primary-foreground text-primary'
                          : 'bg-accent text-accent-foreground'
                      }`}>
                        {item?.badge}
                      </span>
                    )}
                  </>
                )}
                
                {/* Tooltip for collapsed state */}
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-elevation-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                    <div className="font-medium">{item?.label}</div>
                    <div className="text-xs text-muted-foreground">{item?.description}</div>
                  </div>
                )}
              </Link>
            ))}
          </nav>

          {/* User Status */}
          <div className="p-3 border-t border-border">
            {isCollapsed ? (
              <div className="flex justify-center">
                <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">Online</div>
                  <div className="text-xs text-muted-foreground">Ready for patients</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
      {/* Mobile Sidebar */}
      <div className="lg:hidden">
        {/* Mobile Sidebar Overlay */}
        {isMobileOpen && (
          <div 
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setIsMobileOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <aside className={`fixed left-0 top-16 bottom-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="text-sm font-medium text-muted-foreground">Navigation</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileOpen(false)}
                className="h-8 w-8"
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 p-4 space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-clinical ${
                    (location?.pathname + location?.search) === item?.path
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <div className="ml-3 flex-1">
                    <div>{item?.label}</div>
                    <div className="text-xs opacity-75">{item?.description}</div>
                  </div>
                  {item?.badge && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      location?.pathname === item?.path
                        ? 'bg-primary-foreground text-primary'
                        : 'bg-accent text-accent-foreground'
                    }`}>
                      {item?.badge}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </div>
        </aside>
      </div>
      {/* Mobile Menu Trigger (for external use) */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed bottom-4 right-4 z-30 bg-primary text-primary-foreground shadow-elevation-2"
      >
        <Icon name="Menu" size={18} />
      </Button>
    </>
  );
};

export default Sidebar;